import { ArrowUpOutlined } from "@ant-design/icons";
import { Result } from "antd";
import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import ProjectSelector from "../components/ProjectSelector";
import { useAuth } from "../contexts/Auth";
import { UserActionProvider } from "../contexts/UserActions";
import { Scope } from "../generated/types";
import ErrorPage from "../pages/ErrorPage";
import HomePage from "../pages/HomePage";
import LoadingPage from "../pages/LoadingPage";
import SignInPage from "../pages/SignInPage";
import { Project } from "../types";
import apps from "./apps";
import { useAppState } from "./state";
import styles from "./styles.module.css";

const SelectProjectPrompt = () => (
  <div className={styles["select-project-prompt"]}>
    <Result
      icon={<ArrowUpOutlined />}
      title="Please select a project to continue..."
    />
  </div>
);

const RequireProject = (props: React.PropsWithChildren<any>) => {
  const appState = useAppState();

  if (appState.project == null) {
    return <SelectProjectPrompt />;
  }

  return props.children;
};

// Helper component to maintain the same structure as original code
interface RouteContentProps {
  app: {
    selectProject: boolean;
    Page: React.ComponentType<any>;
  };
  appState: {
    project: Project | null;
    setProject: (project: Project | null) => void;
  };
}

const RouteContent = ({ app, appState }: RouteContentProps) => {
  if (app.selectProject) {
    return (
      <>
        <ProjectSelector
          customer={appState.project?.customer}
          project={appState.project?.project}
          scope={Scope.Internal}
          hideScope
          onChange={appState.setProject}
        />

        <RequireProject>
          <UserActionProvider>
            <app.Page />
          </UserActionProvider>
        </RequireProject>
      </>
    );
  } else {
    return <app.Page />;
  }
};

const AppContent = () => {
  const auth = useAuth();
  const appState = useAppState();

  if (auth.status === "loading") {
    return <LoadingPage />;
  }

  if (auth.status === "error") {
    return <ErrorPage error={auth.error} />;
  }

  if (auth.status === "unauthenticated") {
    return <SignInPage />;
  }

  if (appState.app == null) {
    return <HomePage />;
  }

  // Generate routes for all apps
  return (
    <Routes>
      {/* Add routes for each app configuration */}
      {Object.entries(apps).map(([appKey, appConfig]) => (
        <Route
          key={appKey}
          path={
            appConfig.selectProject
              ? `${appConfig.route}/:customer?/:project?`
              : appConfig.route
          }
          element={
            appState.app === appKey ? (
              <RouteContent app={appConfig} appState={appState} />
            ) : (
              // If navigating to a different app than the current one in state,
              // just render the current app's content (state.tsx will handle navigation)
              <></>
            )
          }
        />
      ))}

      {/* Add a fallback route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppContent;
